import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Mail, 
  Users, 
  TrendingUp, 
  Clock,
  Target,
  Activity,
  RefreshCw,
  X
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface EmailSendsMetrics {
  totalSends: number;
  uniqueSends: number;
  sendsToday: number;
  sendsThisWeek: number;
  uniqueRecipients: number;
  uniqueCampaigns: number;
  lastSendTime: string | null;
  topCampaigns: Array<{
    campaign_name: string;
    count: number;
    variants: string[];
    version?: string;
  }>;
}

export default function EmailSendsTracker() {
  const [metrics, setMetrics] = useState<EmailSendsMetrics>({
    totalSends: 0,
    uniqueSends: 0,
    sendsToday: 0,
    sendsThisWeek: 0,
    uniqueRecipients: 0,
    uniqueCampaigns: 0,
    lastSendTime: null,
    topCampaigns: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<{
    campaign_name: string;
    count: number;
    variants: string[];
    version?: string;
  } | null>(null);
  const [variantBreakdown, setVariantBreakdown] = useState<Array<{
    variant: string;
    positiveReplies: number;
  }>>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchVariantBreakdown = async (campaign: { campaign_name: string; variants: string[] }) => {
    try {
      console.log('🔍 [EmailSendsTracker] Fetching variant breakdown for:', campaign.campaign_name);
      
      // Fetch positive replies for each variant
      const variantPromises = campaign.variants.map(async (variant) => {
        const { data, error } = await supabase
          .from('obsync_dynamic_reports')
          .select('campaign_name, reply_category_type')
          .eq('event_type', 'EMAIL_REPLY')
          .eq('reply_count', 1)
          .eq('reply_category_type', 'positive')
          .eq('campaign_name', variant)
          .gte('created_at', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString());

        if (error) {
          console.error(`Error fetching data for variant ${variant}:`, error);
          return { variant, positiveReplies: 0 };
        }

        return { variant, positiveReplies: data?.length || 0 };
      });

      const results = await Promise.all(variantPromises);
      setVariantBreakdown(results);
      console.log('🔍 [EmailSendsTracker] Variant breakdown results:', results);
    } catch (error) {
      console.error('Error fetching variant breakdown:', error);
      setVariantBreakdown([]);
    }
  };

  const handleCampaignClick = (campaign: { campaign_name: string; count: number; variants: string[]; version?: string }) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
    fetchVariantBreakdown(campaign);
  };

  const fetchSendsMetrics = async () => {
    try {
      setIsLoading(true);

      // Get current time boundaries
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Fetch all sends from obsync_dynamic_reports
      const { data: sendsData, error: sendsError } = await supabase
        .from('obsync_dynamic_reports')
        .select('id, created_at, from_email, to_email, campaign_name, campaign_id')
        .order('created_at', { ascending: false });

      if (sendsError) throw sendsError;

      // Process metrics
      const totalSends = sendsData?.length || 0;
      const uniqueSends = new Set(sendsData?.map(s => `${s.from_email}-${s.to_email}-${s.campaign_name}`)).size;
      const sendsToday = sendsData?.filter(s => new Date(s.created_at) >= todayStart).length || 0;
      const sendsThisWeek = sendsData?.filter(s => new Date(s.created_at) >= weekStart).length || 0;
      const uniqueRecipients = new Set(sendsData?.map(s => s.to_email)).size;
      const uniqueCampaigns = new Set(sendsData?.map(s => s.campaign_name).filter(Boolean)).size;
      const lastSendTime = sendsData?.[0]?.created_at || null;

      // Get top campaigns by positive replies instead of sends
      // First, get positive replies data with campaign names and campaign_id
      const { data: positiveRepliesData, error: positiveRepliesError } = await supabase
        .from('obsync_dynamic_reports')
        .select('campaign_name, campaign_id, reply_category_type')
        .eq('event_type', 'EMAIL_REPLY')
        .eq('reply_count', 1)
        .eq('reply_category_type', 'positive')
        .gte('created_at', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString());

      if (positiveRepliesError) {
        console.error('Error fetching positive replies for top campaigns:', positiveRepliesError);
      }

      // Create a mapping from Smartlead campaign ID to main campaign info
      const smartleadToMainCampaign: Record<string, { name: string; version?: string }> = {};
      
      // Simple fallback: extract main campaign name from variant name
      // This is more reliable than complex Supabase joins
      console.log('🔍 [EmailSendsTracker] Using fallback mapping logic based on variant names');
      
      positiveRepliesData?.forEach(reply => {
        const variantName = reply.campaign_name || 'Unknown Campaign';
        const smartleadId = reply.campaign_id?.toString();
        
        // Extract main campaign name from variant name
        let mainCampaignName = variantName;
        if (variantName.includes('Black Swan')) {
          mainCampaignName = 'Black Swan';
        } else if (variantName.includes('LP-Bulk-No Returns') || variantName.includes('LP-Bulk-Has Returns')) {
          mainCampaignName = 'LP - Bulk - Returns';
        } else if (variantName.includes('Lost Ops')) {
          mainCampaignName = 'Lost Ops - Historical';
        } else if (variantName.includes('LP-Bulk-No Wonderment') || variantName.includes('LP-Bulk-Has Wonderment')) {
          mainCampaignName = 'LP - Bulk - Wonderment';
        } else if (variantName.includes('YP - Bulk - Reviews')) {
          mainCampaignName = 'YP - Bulk - Reviews';
        } else if (variantName.includes('YP - Bulk - Loyalty')) {
          mainCampaignName = 'YP - Bulk - Loyalty';
        }
        
        if (smartleadId) {
          // Extract version from variant name (e.g., V1, V4, V5)
          const versionMatch = variantName.match(/V(\d+)/);
          const version = versionMatch ? versionMatch[1] : '1';
          
          smartleadToMainCampaign[smartleadId] = {
            name: mainCampaignName,
            version: version
          };
        }
      });
      
      console.log('🔍 [EmailSendsTracker] Fallback mapping created:', smartleadToMainCampaign);

      // Group positive replies by main campaign name using Smartlead campaign ID
      const campaignCounts: Record<string, { count: number; variants: Set<string>; version?: string }> = {};
      
      console.log('🔍 [EmailSendsTracker] Debugging campaign mapping:', {
        positiveRepliesData: positiveRepliesData?.slice(0, 3), // Show first 3 for debugging
        smartleadToMainCampaign: Object.keys(smartleadToMainCampaign).slice(0, 5), // Show first 5 keys
        sampleMapping: Object.entries(smartleadToMainCampaign).slice(0, 3) // Show first 3 entries
      });
      
      // Count positive replies per campaign
      positiveRepliesData?.forEach(reply => {
        const variantName = reply.campaign_name || 'Unknown Campaign';
        const smartleadId = reply.campaign_id?.toString();
        const mainCampaignInfo = smartleadId ? smartleadToMainCampaign[smartleadId] : null;
        const mainCampaignName = mainCampaignInfo?.name || variantName;
        
        console.log('🔍 [EmailSendsTracker] Processing reply:', {
          variantName,
          smartleadId,
          mainCampaignInfo,
          mainCampaignName,
          mappingExists: !!mainCampaignInfo
        });
        
        if (!campaignCounts[mainCampaignName]) {
          campaignCounts[mainCampaignName] = { 
            count: 0, 
            variants: new Set(), 
            version: mainCampaignInfo?.version || variantName.split('-').pop() || '1'
          };
        }
        
        campaignCounts[mainCampaignName].count += 1;
        campaignCounts[mainCampaignName].variants.add(variantName);
      });

      const topCampaigns = Object.entries(campaignCounts)
        .sort(([,a], [,b]) => b.count - a.count)
        .slice(0, 5)
        .map(([campaign_name, data]) => ({ 
          campaign_name, 
          count: data.count,
          variants: Array.from(data.variants),
          version: data.version
        }));

      console.log('🎯 [EmailSendsTracker] Final top campaigns:', topCampaigns);

      setMetrics({
        totalSends,
        uniqueSends,
        sendsToday,
        sendsThisWeek,
        uniqueRecipients,
        uniqueCampaigns,
        lastSendTime,
        topCampaigns
      });

    } catch (error) {
      console.error('Error fetching email sends metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSendsMetrics();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSendsMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      {/* Top Campaigns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Top Performing Campaigns (by Positive Replies)
          </CardTitle>
        </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {metrics.topCampaigns.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No campaign data available
                    </div>
                  ) : (
                    metrics.topCampaigns.map((campaign, index) => (
                      <div 
                        key={campaign.campaign_name} 
                        className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleCampaignClick(campaign)}
                      >
                        <div className="flex items-start gap-4 flex-1">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                            {index + 1}
                          </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-semibold text-base text-gray-900">
                          {campaign.campaign_name}
                        </div>
                        {campaign.version && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            v{campaign.version}
                          </span>
                        )}
                      </div>
                      {campaign.variants.length > 0 && (
                        <div className="space-y-1">
                          {campaign.variants.slice(0, 2).map((variant, idx) => (
                            <div key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border-l-2 border-gray-200">
                              {variant}
                            </div>
                          ))}
                          {campaign.variants.length > 2 && (
                            <div className="text-xs text-gray-500 italic">
                              +{campaign.variants.length - 2} more variants
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {campaign.count.toLocaleString()} positive replies
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Variant Breakdown Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              {selectedCampaign?.campaign_name} - Variant Breakdown
              {selectedCampaign?.version && (
                <Badge variant="outline" className="ml-2">
                  v{selectedCampaign.version}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-900">Total Positive Replies</h3>
                  <p className="text-2xl font-bold text-blue-600">{selectedCampaign?.count.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-blue-700">Across {selectedCampaign?.variants.length} variants</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Positive Replies by Variant:</h4>
              {variantBreakdown.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading variant breakdown...
                </div>
              ) : (
                <div className="space-y-2">
                  {variantBreakdown
                    .sort((a, b) => b.positiveReplies - a.positiveReplies)
                    .map((variant, index) => (
                      <div key={variant.variant} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0 w-6 h-6 bg-gray-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {variant.variant}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {variant.positiveReplies} positive replies
                          </Badge>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

